<template>
  <div class="base-sheet-checklist">
    <input
      v-if="choices.length > searchFrom"
      v-model="query"
      type="search"
      enterkeyhint="search"
      class="base-sheet-checklist__search"
      :placeholder="$t('search-bar.placeholder')"
      :aria-label="$t('search-bar.placeholder')"
      @keydown.enter="closeKeyboardOnTouch($event.target)"
    />
    <ul class="base-bottom-sheet__menu" role="group" :aria-label="label">
      <li v-for="choice in shownChoices" :key="choice.id">
        <button
          type="button"
          role="checkbox"
          :aria-checked="String(value.includes(choice.id))"
          @click="toggle(choice.id)"
        >
          <span class="base-sheet-checklist__box" :class="{ 'is-checked': value.includes(choice.id) }">
            <BaseIcon name="check" />
          </span>
          <slot :choice="choice">
            <span class="base-sheet-checklist__text">
              {{ choice.name }}
              <span v-if="choice.detail" class="base-sheet-checklist__detail">{{ choice.detail }}</span>
            </span>
          </slot>
        </button>
      </li>
    </ul>
    <p v-if="!shownChoices.length" class="base-sheet-checklist__empty">{{ $t('form-auto-complete.no-results') }}</p>
    <div class="base-sheet-checklist__foot">
      <button type="button" class="base-sheet-checklist__done" @click="$emit('done')">{{ $t('actions.done') }}</button>
    </div>
  </div>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';
import { closeKeyboardOnTouch } from '@/util/search-key';

// Several choices to tick in a bottom sheet (labels, assignees), with a search once the list
// is long. The sheet saves the picks when it closes, by "Terminé" or a tap outside, as the
// desktop menus do when they close.
export default {
  name: 'BaseSheetChecklist',
  components: { BaseIcon },
  props: {
    // `{ id, name, detail }` each, `detail` (a person's email) under the name; the default slot
    // shows a choice otherwise.
    choices: {
      type: Array,
      required: true,
    },
    // The ids ticked.
    value: {
      type: Array,
      default: () => [],
    },
    // What the choices are, for screen readers.
    label: {
      type: String,
      default: '',
    },
    // The words a choice is found by.
    searchText: {
      type: Function,
      default: (choice) => [choice.name, choice.detail].filter(Boolean).join(' '),
    },
    // Past this many choices, a search field helps.
    searchFrom: {
      type: Number,
      default: 8,
    },
  },
  data() {
    return {
      query: '',
    };
  },
  computed: {
    shownChoices() {
      const query = this.query.trim().toLowerCase();
      if (!query) return this.choices;
      return this.choices.filter((choice) => this.searchText(choice).toLowerCase().includes(query));
    },
  },
  methods: {
    closeKeyboardOnTouch,
    toggle(id) {
      this.$emit('input', this.value.includes(id) ? this.value.filter((picked) => picked !== id) : [...this.value, id]);
    },
  },
};
</script>

<style lang="scss">
.base-sheet-checklist {
  &__search {
    width: 100%;
    min-height: 44px;
    margin-bottom: 4px;
    padding: 0 12px;
    border: 1px solid $color-border;
    border-radius: $border-radius-root;
    background: $color-slate-50;
    font-size: 16px;
  }

  &__box {
    display: inline-flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: 2px solid $color-slate-300;
    border-radius: $radius-sm;
    color: transparent;

    &.is-checked {
      border-color: $color-accent;
      background: $color-accent;
      color: $color-white;
    }
  }

  // Over the sheet menu's own icon size.
  .base-bottom-sheet__menu &__box .base-icon {
    width: 14px;
    height: 14px;
  }

  &__text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__detail {
    overflow: hidden;
    font-size: 12px;
    color: $color-slate-500;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__empty {
    margin: 8px 4px;
    font-size: 14px;
    color: $color-slate-500;
  }

  // Stays in reach at the foot of a long list, on a white band the list scrolls under, as in the
  // filter chips' sheets. It takes the sheet's bottom padding (BaseBottomSheet) as its own, to keep
  // it below the button while the list scrolls.
  &__foot {
    position: sticky;
    bottom: 0;
    margin: 8px 0 calc(-12px - env(safe-area-inset-bottom, 0px));
    padding: 10px 0 calc(12px + env(safe-area-inset-bottom, 0px));
    border-top: 1px solid $color-border;
    background: $color-white;
  }

  &__done {
    width: 100%;
    min-height: 44px;
    border-radius: $radius-full;
    background: $color-accent;
    font-size: 15px;
    font-weight: 500;
    color: $color-white;
  }
}
</style>
